/**
 * Media Constants
 * 
 * Shared constants for media types, image URLs, and common configurations
 * to reduce code duplication across the codebase
 */

// Media Types - Single source of truth
export const MEDIA_TYPES = {
  MOVIE: 'movie',
  TV: 'tv',
};

// TMDB Image Base URLs - Used 24+ times across codebase
export const TMDB_IMAGE_URLS = {
  BASE: 'https://image.tmdb.org/t/p',
  W92: 'https://image.tmdb.org/t/p/w92',
  W154: 'https://image.tmdb.org/t/p/w154',
  W185: 'https://image.tmdb.org/t/p/w185',
  W342: 'https://image.tmdb.org/t/p/w342',
  W500: 'https://image.tmdb.org/t/p/w500',
  W780: 'https://image.tmdb.org/t/p/w780',
  W1280: 'https://image.tmdb.org/t/p/w1280',
  ORIGINAL: 'https://image.tmdb.org/t/p/original',
};

// Image Size Variants
export const IMAGE_SIZES = {
  POSTER: {
    SMALL: 'w185',
    MEDIUM: 'w342',
    LARGE: 'w500',
    ORIGINAL: 'original',
  },
  BACKDROP: {
    SMALL: 'w780',
    MEDIUM: 'w1280',
    LARGE: 'original',
  },
  PROFILE: {
    SMALL: 'w185',
    MEDIUM: 'w500',
  },
  LOGO: {
    SMALL: 'w92',
    MEDIUM: 'w154',
    LARGE: 'w185',
    ORIGINAL: 'original',
  },
};

// Helper function to build image URL
export function buildImageUrl(path, size = 'w500') {
  if (!path) return null;
  return `${TMDB_IMAGE_URLS.BASE}/${size}${path}`;
}

// Media Type Helpers
export function isMovie(mediaType) {
  return mediaType === MEDIA_TYPES.MOVIE;
}

export function isTVShow(mediaType) {
  return mediaType === MEDIA_TYPES.TV;
}

// Get media title based on type
export function getMediaTitle(media, mediaType) {
  return isTVShow(mediaType) ? media.name : media.title;
}

// Get media release date based on type
export function getMediaReleaseDate(media, mediaType) {
  if (isTVShow(mediaType)) {
    // Try first_air_date first, fallback to release_year if not available (MDBList format)
    return media.first_air_date || (media.release_year ? `${media.release_year}-01-01` : null);
  }
  // For movies, try release_date first, fallback to release_year
  return media.release_date || (media.release_year ? `${media.release_year}-01-01` : null);
}

// Media type display names (Lithuanian)
export const MEDIA_TYPE_LABELS = {
  [MEDIA_TYPES.MOVIE]: 'filmo',
  [MEDIA_TYPES.TV]: 'TV serialo',
};

// Query/Cache Configuration - Used in multiple places
export const QUERY_CONFIG = {
  STALE_TIME: 5 * 60 * 1000, // 5 minutes
  GC_TIME: 30 * 60 * 1000, // 30 minutes (cache time)
  REFETCH_ON_WINDOW_FOCUS: false,
  REFETCH_ON_MOUNT: false,
  RETRY: 2,
  RETRY_DELAY: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
};

// Ratings Configuration
export const RATINGS_CONFIG = {
  MIN_VOTE_COUNT: 3000, // Minimum votes for TMDB-only sorting
  BULK_FETCH_ENABLED: true,
  CACHE_TIME: 10 * 60 * 1000, // 10 minutes
};
